import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle,
  ChevronDown,
  FileCheck,
  FolderOpen,
  Home,
  MailPlus,
  MessageSquare,
  Pencil,
  Receipt,
  Send,
  UserPlus,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { AppDialogFrame } from '../../../components/overlays/AppDialogFrame';
import { ModuleSectionNav, type ModuleSectionNavItem } from '../../../components/navigation/ModuleSectionNav';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import {
  useGetFinancialAidApplicationForReviewQuery,
  useGetFinancialAidStatusHistoryQuery,
  useGetSettingsModuleConfigQuery,
  type FinancialAidReviewDetail,
  type FinancialAidStatusHistoryResponse,
} from '../../../state/domainRouterApi';
import {
  useAssignFinancialAidApplication,
  useMakeFinancialAidDecision,
  useRequestFinancialAidMoreInfo,
  useUpdateFinancialAidApplication,
} from '../../../financial-aid/hooks/useFinancialAidApi';
import { asArray, asRecord } from '../utils/domainData';
import {
  FinancialAidCorrectionEditorPanel,
  FinancialAidCorrectionHeaderActions,
  useFinancialAidCorrectionEditor,
  type FinancialAidCorrectionEditorController,
} from '@triedge/financial-aid-ui/corrections';
import {
  isFinancialAidAssignedStaffUserId,
  type FinancialAidApplication,
} from '@triedge/financial-aid-domain';
import {
  AWARD_PERCENT_MAX,
  AWARD_PERCENT_MIN,
  awardAmountToPercent,
  formatAwardAmountInput,
  formatAwardCurrency,
  formatAwardPercentInput,
  formatAwardPercentLabel,
  isSameAwardPercent,
  parseAwardInput,
  percentToAwardAmount,
  resolveAwardEntryDecision,
  roundAwardPercent,
} from './financialAidAwardEntry';
import {
  publishFinancialAidReportStaleSignal,
  readFinancialAidReportStaleSignal,
  subscribeFinancialAidReportStaleSignal,
  type FinancialAidReportStaleSignal,
} from '../reportStaleSignals';

const readText = (value: unknown, defaultValue = ''): string => {
  const next = String(value ?? '').trim();
  return next || defaultValue;
};

const isStaffUserId = (value: unknown): value is string => {
  const normalized = readText(value);
  return isFinancialAidAssignedStaffUserId(normalized);
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeFinancialAidTimestamp = (value: unknown): number | null => {
  const numeric = asNumber(value);
  if (numeric == null) {
    return null;
  }
  return numeric > 10_000_000_000 ? Math.floor(numeric / 1000) : numeric;
};

const toEpochMs = (value: unknown): number | null => {
  const numeric = asNumber(value);
  if (numeric != null) {
    return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
  }
  const raw = readText(value);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDate = (value: unknown): string => {
  const ms = toEpochMs(value);
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString('en-US');
  } catch {
    return '';
  }
};

const normalizeStatusToken = (value: unknown): string => {
  const raw = readText(value).toUpperCase().replace(/[\s-]+/g, '_');
  if (!raw) return 'NEW';
  if (raw === 'DECLINED') return 'DENIED';
  if (raw === 'UNDER_REVIEW') return 'IN_PROGRESS';
  return raw;
};

const toStatusLabel = (token: string): string => token.replace(/_/g, ' ');
const canRequestMoreInfoFromStatus = (token: string): boolean =>
  token === 'SUBMITTED' || token === 'IN_PROGRESS' || token === 'UNDER_REVIEW';
const isRequestMoreInfoTransitionError = (message: string): boolean =>
  /invalid transition|cannot transition/i.test(message);
const readRequestMoreInfoTransitionStatus = (message: string): string => {
  const match = message.match(/(?:from|Status)\s+'([^']+)'/i);
  return match ? normalizeStatusToken(match[1]) : '';
};
const buildRequestMoreInfoUnavailableMessage = (statusToken: string): string => {
  const statusLabel = toStatusLabel(statusToken || 'NEW').toLowerCase();
  return `Request More Info is only available for submitted or under-review applications. This application is currently ${statusLabel}.`;
};

const readLatestStaffCorrectionTimestamp = (sectionsValue: unknown): number | null => {
  const additionalInfo = asRecord(asRecord(sectionsValue).additionalInfo);
  return asArray(asRecord(additionalInfo).staffCorrections).reduce<number | null>((latest, entry) => {
    const correctedAt = normalizeFinancialAidTimestamp(asRecord(entry).correctedAt);
    if (correctedAt == null) {
      return latest;
    }
    return latest == null ? correctedAt : Math.max(latest, correctedAt);
  }, null);
};

const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  APPLIED: { bg: '#ECFDF5', text: '#237062', dot: '#28E2A7' },
  IN_PROGRESS: { bg: '#ECFDF5', text: '#237062', dot: '#2697A2' },
  SCHEDULED: { bg: '#FEE2E2', text: '#FE2C3B', dot: '#FF5F5F' },
  DECISION: { bg: '#ECFDF5', text: '#237062', dot: '#92EEBA' },
  APPROVED: { bg: '#ECFDF5', text: '#237062', dot: '#92EEBA' },
  ACCEPTED: { bg: '#ECFDF5', text: '#237062', dot: '#28E2A7' },
  DENIED: { bg: '#FEE2E2', text: '#FE2C3B', dot: '#FE2C3B' },
  NEW: { bg: '#ECFDF5', text: '#237062', dot: '#28E2A7' },
};

const DETAIL_COMPACT_TRIGGER_WIDTH_PX = 768;
const DETAIL_HEADER_STACK_WIDTH_PX = 1280;
type DetailLayoutMode = 'compact' | 'full';
type DecisionEmailKind = 'approve' | 'deny';
type DecisionSubmitOptions = {
  sendExternalParentEmail?: boolean;
};

type DecisionEmailPreview = {
  kind: DecisionEmailKind;
  to: string;
  eventType: string;
  statusLabel: string;
  actionHeading: string;
  actionLabel: string;
  subject: string;
  message: string;
  actionBody: string;
};

const buildExternalParentEmailApprovalPayload = (sendExternalParentEmail: boolean) => (
  sendExternalParentEmail
    ? {
      sendExternalParentEmail: true,
      externalParentEmailApproved: true,
    }
    : {}
);

const Toast = ({
  message,
  tone,
  onClose,
}: {
  message: string;
  tone: 'success' | 'error';
  onClose: () => void;
}) => (
  <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[200] animate-in slide-in-from-top-4 duration-300">
    <div
      className={`flex items-center gap-3 px-6 py-3 rounded-2xl shadow-2xl ${
        tone === 'success' ? 'bg-primary text-white' : 'bg-strawberry text-white'
      }`}
    >
      {tone === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
      <p className="text-sm font-bold">{message}</p>
      <button type="button" onClick={onClose} className="ml-2 p-1 hover:bg-white/10 rounded-lg transition-colors">
        <X size={16} />
      </button>
    </div>
  </div>
);

export type FinancialAidStaffDetailContext = {
  applicationId: string;
  reportStaleAfter: number | null;
  review: FinancialAidReviewDetail | null;
  statusHistory: FinancialAidStatusHistoryResponse | null;
  layoutMode: DetailLayoutMode;
  refetch: () => Promise<void>;
  isCorrectionEditing: boolean;
  correctionReason: string;
  draftSections: Record<string, unknown>;
  setCorrectionReason: (value: string) => void;
  updateCorrectionSection: (sectionKey: string, patch: unknown) => void;
  beginCorrectionEditing: () => void;
  correctionEditor: ReactNode;
  correctionController: FinancialAidCorrectionEditorController;
};

type SidebarTab = {
  id: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
};

export function ApplicantDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId: rawId } = useParams<{ applicationId: string }>();
  const applicationId = readText(rawId);
  const detailRootRef = useRef<HTMLElement | null>(null);
  const assignMenuRef = useRef<HTMLDivElement | null>(null);

  const detail = useGetFinancialAidApplicationForReviewQuery(applicationId, { skip: !applicationId });
  const history = useGetFinancialAidStatusHistoryQuery(applicationId, { skip: !applicationId });
  const makeDecision = useMakeFinancialAidDecision();
  const assignMutation = useAssignFinancialAidApplication();
  const requestMoreInfoMutation = useRequestFinancialAidMoreInfo();
  const updateApplication = useUpdateFinancialAidApplication();
  const settingsConfig = useGetSettingsModuleConfigQuery({ moduleKey: 'staff_settings' });

  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isAssignMenuOpen, setIsAssignMenuOpen] = useState(false);
  const [optimisticAssigneeName, setOptimisticAssigneeName] = useState<string | null>(null);
  const [isMoreInfoMode, setIsMoreInfoMode] = useState(false);
  const [moreInfoMessage, setMoreInfoMessage] = useState('');
  const [moreInfoEmailConfirmed, setMoreInfoEmailConfirmed] = useState(false);
  const [moreInfoErrorMessage, setMoreInfoErrorMessage] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [busyAction, setBusyAction] = useState<'approve' | 'deny' | 'more-info' | null>(null);
  const [optimisticStatusToken, setOptimisticStatusToken] = useState<string | null>(null);
  const [optimisticReportStaleAfter, setOptimisticReportStaleAfter] = useState<number | null>(null);
  const [decisionEmailPreviewKind, setDecisionEmailPreviewKind] = useState<DecisionEmailKind | null>(null);
  const [decisionEmailConfirmed, setDecisionEmailConfirmed] = useState(false);
  const [awardPercentInput, setAwardPercentInput] = useState('');
  const [awardAmountInput, setAwardAmountInput] = useState('');
  const [awardJustification, setAwardJustification] = useState('');
  const [awardErrorMessage, setAwardErrorMessage] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [detailSurfaceWidth, setDetailSurfaceWidth] = useState(0);

  const review = detail.data ?? null;
  const statusHistory = history.data ?? null;
  const reviewRecord = asRecord(review as unknown);
  const applicationRecord = asRecord(reviewRecord.application);
  const sections = asRecord(applicationRecord.sections);
  const additionalInfo = asRecord(sections.additionalInfo);
  const familyInformation = asRecord(sections.familyInformation);
  const parent1 = asRecord(sections.parent1);
  const parent2 = asRecord(sections.parent2);
  const recommendation = asRecord(reviewRecord.recommendation);
  const contractFlags = asRecord(sections.contractFlags);
  const metrics = asRecord(applicationRecord.metrics);
  // INT-1127: the decision backend derives awardedAmount from
  // metrics.ystEligibleTuition, so the modal converts dollars against the same
  // figure to keep the displayed and stored award in agreement.
  const eligibleTuition = Math.max(0, asNumber(metrics.ystEligibleTuition) ?? 0);
  const hasEligibleTuition = eligibleTuition > 0;

  const familyName = readText(
    familyInformation.familyName,
    readText(applicationRecord.familyName, readText(applicationRecord.code, 'Family'))
  );
  const code = readText(applicationRecord.code, applicationId);
  const statusToken = normalizeStatusToken(applicationRecord.status);
  const displayStatusToken = optimisticStatusToken ?? statusToken;
  const submittedDate = formatDate(
    applicationRecord.submittedAt ?? applicationRecord.createdAt ?? applicationRecord.updatedAt
  );
  const persistedAssigneeName = readText(
    applicationRecord.assigneeName,
    readText(asRecord(applicationRecord.assignee).name, '')
  );
  const assigneeName = optimisticAssigneeName !== null ? optimisticAssigneeName : persistedAssigneeName;
  const persistedReportStaleAfter = useMemo(
    () => readLatestStaffCorrectionTimestamp(sections),
    [sections],
  );
  const reportStaleAfter = optimisticReportStaleAfter == null
    ? persistedReportStaleAfter
    : Math.max(persistedReportStaleAfter ?? optimisticReportStaleAfter, optimisticReportStaleAfter);

  useEffect(() => {
    setOptimisticAssigneeName(null);
  }, [persistedAssigneeName]);

  useEffect(() => {
    setOptimisticReportStaleAfter(null);
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId) {
      return undefined;
    }
    const applyStaleSignal = (signal: FinancialAidReportStaleSignal) => {
      setOptimisticReportStaleAfter((current) => Math.max(current ?? signal.staleAfter, signal.staleAfter));
    };
    const storedSignal = readFinancialAidReportStaleSignal(applicationId);
    if (storedSignal) {
      applyStaleSignal(storedSignal);
    }
    return subscribeFinancialAidReportStaleSignal(applicationId, applyStaleSignal);
  }, [applicationId]);

  useEffect(() => {
    if (
      optimisticReportStaleAfter != null
      && persistedReportStaleAfter != null
      && persistedReportStaleAfter >= optimisticReportStaleAfter
    ) {
      setOptimisticReportStaleAfter(null);
    }
  }, [optimisticReportStaleAfter, persistedReportStaleAfter]);

  const staffAssignOptions = useMemo(() => {
    const payload = asRecord(asRecord(settingsConfig.data).payload);
    const staffRows = Array.isArray(payload.staff) ? payload.staff : [];
    const options: Array<{ id: string | null; name: string }> = [];
    staffRows.forEach((entry) => {
      const record = asRecord(entry);
      const name = readText(
        record.displayName,
        readText(record.name, `${readText(record.firstName)} ${readText(record.lastName)}`.trim()),
      );
      if (!name) return;
      const rawId =
        readText(record.id) ||
        readText(record.userId) ||
        readText(record.staffId);
      const id = isStaffUserId(rawId) ? rawId : null;
      options.push({ id, name });
    });
    return options;
  }, [settingsConfig.data]);
  const recommendedPercent =
    asNumber(recommendation.recommendedPercent)
    ?? asNumber(asRecord(applicationRecord.recommendation).recommendedPercent)
    ?? asNumber(asRecord(applicationRecord.finalAward).scholarshipPercent)
    ?? 0;

  // INT-1127: the modal's award fields default to the recommendation but stay
  // editable, so staff can record the scholarship the school actually granted.
  useEffect(() => {
    if (!isCompleteModalOpen) return;
    const defaultPercent = roundAwardPercent(Math.max(0, recommendedPercent));
    setAwardPercentInput(formatAwardPercentInput(defaultPercent));
    setAwardAmountInput(
      eligibleTuition > 0
        ? formatAwardAmountInput(percentToAwardAmount(defaultPercent, eligibleTuition))
        : '',
    );
    setAwardJustification('');
    setAwardErrorMessage('');
  }, [isCompleteModalOpen, recommendedPercent, eligibleTuition]);

  const handleAwardPercentChange = (rawValue: string) => {
    setAwardPercentInput(rawValue);
    setAwardErrorMessage('');
    const parsed = parseAwardInput(rawValue);
    if (parsed == null) {
      setAwardAmountInput('');
      return;
    }
    setAwardAmountInput(
      hasEligibleTuition ? formatAwardAmountInput(percentToAwardAmount(parsed, eligibleTuition)) : '',
    );
  };

  const handleAwardAmountChange = (rawValue: string) => {
    setAwardAmountInput(rawValue);
    setAwardErrorMessage('');
    if (!hasEligibleTuition) return;
    const parsed = parseAwardInput(rawValue);
    if (parsed == null) {
      setAwardPercentInput('');
      return;
    }
    setAwardPercentInput(formatAwardPercentInput(awardAmountToPercent(parsed, eligibleTuition)));
  };

  const parsedAwardPercent = parseAwardInput(awardPercentInput);
  const awardPercentPreview = parsedAwardPercent == null ? null : roundAwardPercent(parsedAwardPercent);
  const awardAmountPreview = awardPercentPreview == null
    ? null
    : percentToAwardAmount(awardPercentPreview, eligibleTuition);
  const isAwardWithinRange = awardPercentPreview != null
    && awardPercentPreview >= AWARD_PERCENT_MIN
    && awardPercentPreview <= AWARD_PERCENT_MAX;
  const isAwardOverride = awardPercentPreview != null
    && !isSameAwardPercent(awardPercentPreview, roundAwardPercent(recommendedPercent));

  const primaryContactName = readText(
    `${readText(parent1.firstName)} ${readText(parent1.lastName)}`,
    readText(`${readText(parent2.firstName)} ${readText(parent2.lastName)}`, 'Not provided')
  );
  const primaryEmail = readText(parent1.email, readText(parent2.email, 'No email on file'));
  const primaryPhone = readText(
    parent1.cellPhone,
    readText(parent1.workPhone, readText(parent2.cellPhone, readText(parent2.workPhone, 'No phone on file')))
  );
  const parent1Email = readText(parent1.email);
  const parent2Email = readText(parent2.email);
  const decisionEmailRecipient = parent1Email || parent2Email;

  const flagCount = useMemo(() => {
    const contractFlagItems = asArray<Record<string, unknown>>(contractFlags.flags).length;
    const recommendationFlagItems = asArray<Record<string, unknown>>(recommendation.flags).length;
    return contractFlagItems || recommendationFlagItems;
  }, [contractFlags.flags, recommendation.flags]);

  const sidebarTabs = useMemo<SidebarTab[]>(
    () => [
      { id: 'family-profile', icon: Users, label: 'Family Profile' },
      { id: 'income-calculation', icon: Building2, label: 'Income & Calculation' },
      { id: 'assets-equity', icon: Home, label: 'Assets & Equity' },
      { id: 'expenses-spending', icon: Receipt, label: 'Expenses & Spending' },
      { id: 'contract-flags', icon: FileCheck, label: 'Contract & Flags', badge: flagCount || undefined },
      { id: 'corrections', icon: Pencil, label: 'Corrections' },
      { id: 'documents', icon: FolderOpen, label: 'Documents' },
      { id: 'notes', icon: MessageSquare, label: 'Notes' },
    ],
    [flagCount]
  );
  const activePathId = location.pathname.split('/').filter(Boolean).pop();
  const activeTab = sidebarTabs.find((tab) => tab.id === activePathId) ?? sidebarTabs[0];
  const isCorrectionsTabActive = activePathId === 'corrections';
  const sectionNavItems = useMemo<ModuleSectionNavItem[]>(
    () => sidebarTabs.map((tab) => ({
      id: tab.id,
      label: tab.label,
      to: tab.id,
      icon: tab.icon,
      badge: tab.badge,
    })),
    [sidebarTabs],
  );
  const isCompactLayout = detailSurfaceWidth > 0 && detailSurfaceWidth <= DETAIL_COMPACT_TRIGGER_WIDTH_PX;
  const shouldStackDetailHeader =
    isCompactLayout || (detailSurfaceWidth > 0 && detailSurfaceWidth <= DETAIL_HEADER_STACK_WIDTH_PX);
  const layoutMode: DetailLayoutMode = isCompactLayout ? 'compact' : 'full';
  const rootLayoutClass = isCompactLayout ? 'flex-col' : 'flex-row';
  const rootWidthClass = isCompactLayout ? 'w-full max-w-[760px] mx-auto' : 'w-full';
  const mobileHeaderClass = isCompactLayout ? 'flex' : 'hidden';
  const sidebarClass = isCompactLayout ? 'hidden' : 'flex';
  const headerPaddingClass = isCompactLayout ? 'px-6 py-4' : 'px-10 py-6';
  const headerContentClass = shouldStackDetailHeader ? 'flex-col' : 'flex-row items-center';
  const titleClass = isCompactLayout ? 'text-xl' : shouldStackDetailHeader ? 'text-3xl leading-tight' : 'text-4xl leading-tight';
  const metaClass = isCompactLayout ? 'text-xs' : 'text-sm';
  const actionRowClass = shouldStackDetailHeader ? 'gap-2 pb-1' : 'gap-2 pb-0 justify-start md:justify-end';
  const actionButtonClass = isCompactLayout ? 'px-3 py-2 text-xs' : 'px-4 py-2 text-[90%]';
  const mainPaddingClass = isCompactLayout ? 'p-4' : 'p-10';
  const titleStatusRowClass = shouldStackDetailHeader
    ? 'flex-col items-start gap-2'
    : 'items-center gap-3 flex-wrap';
  const titleWidthClass = shouldStackDetailHeader
    ? 'w-full'
    : 'min-w-0 max-w-full';

  const refetch = useCallback(async () => {
    await Promise.all([
      detail.refetch(),
      history.refetch(),
    ]);
  }, [detail.refetch, history.refetch]);

  const handleCorrectionUpdate = useCallback(
    async (input: Parameters<typeof updateApplication.mutateAsync>[0]) =>
      await updateApplication.mutateAsync(input) as FinancialAidApplication,
    [updateApplication.mutateAsync],
  );
  const handleCorrectionSaved = useCallback((updatedApplication: FinancialAidApplication) => {
    const updatedRecord = asRecord(updatedApplication);
    const updatedSections = asRecord(updatedRecord.sections);
    const nextReportStaleAfter =
      readLatestStaffCorrectionTimestamp(updatedSections)
        ?? normalizeFinancialAidTimestamp(updatedRecord.updatedAt)
        ?? Math.floor(Date.now() / 1000);
    setOptimisticReportStaleAfter(nextReportStaleAfter);
    publishFinancialAidReportStaleSignal({
      applicationId,
      staleAfter: nextReportStaleAfter,
    });
  }, [applicationId]);
  const correctionController = useFinancialAidCorrectionEditor({
    application: review
      ? reviewRecord.application as FinancialAidApplication
      : null,
    includeTenantExtensions: true,
    onUpdate: handleCorrectionUpdate,
    onRefresh: refetch,
    onSaved: handleCorrectionSaved,
    onSuccess: (message) => setToast({ tone: 'success', message }),
    onError: (message) => setToast({ tone: 'error', message }),
  });
  const {
    isEditing: isCorrectionEditing,
    correctionReason,
    draftSections,
    setCorrectionReason,
    updateCorrectionSection,
    beginEditing: beginCorrectionEditing,
  } = correctionController;
  const correctionEditor = (
    <FinancialAidCorrectionEditorPanel
      controller={correctionController}
      showFieldList={isCorrectionsTabActive}
    />
  );

  const ctx: FinancialAidStaffDetailContext = useMemo(
    () => ({
      applicationId,
      reportStaleAfter,
      review,
      statusHistory,
      layoutMode,
      refetch,
      isCorrectionEditing,
      correctionReason,
      draftSections,
      setCorrectionReason,
      updateCorrectionSection,
      beginCorrectionEditing,
      correctionEditor,
      correctionController,
    }),
    [
      applicationId,
      beginCorrectionEditing,
      correctionEditor,
      correctionReason,
      correctionController,
      draftSections,
      isCorrectionEditing,
      layoutMode,
      refetch,
      reportStaleAfter,
      review,
      statusHistory,
      updateCorrectionSection,
    ],
  );

  const decisionEmailPreview = useMemo<DecisionEmailPreview | null>(() => {
    if (!decisionEmailPreviewKind) return null;
    const isApproval = decisionEmailPreviewKind === 'approve';
    const eventType = isApproval ? 'Award Decision Ready' : 'Decision Notice';
    const statusLabel = isApproval ? 'Approved' : 'Decision Posted';
    const actionHeading = isApproval ? 'Award available' : 'Decision posted';
    const actionLabel = isApproval ? 'Review award' : 'Review decision';
    const applicationCode = code || applicationId || 'Application';
    return {
      kind: decisionEmailPreviewKind,
      to: decisionEmailRecipient || 'No parent email on file',
      eventType,
      statusLabel,
      actionHeading,
      actionLabel,
      subject: `${eventType} — ${familyName} (${applicationCode})`,
      message: isApproval
        ? `A financial aid decision is ready for the ${familyName} family application (${applicationCode}). Please review it in the parent portal.`
        : `A financial aid decision is ready for the ${familyName} family application (${applicationCode}). Please review the decision letter and next steps in the parent portal.`,
      actionBody: isApproval
        ? 'Your financial aid decision is ready for review in the portal.'
        : 'A financial aid decision has been posted to your portal.',
    };
  }, [applicationId, code, decisionEmailPreviewKind, decisionEmailRecipient, familyName]);

  const moreInfoEmailPreview = useMemo(() => {
    const applicationCode = code || applicationId || 'Application';
    const requestedInfo = moreInfoMessage.trim()
      || 'Describe the documents or details the family should provide.';
    return {
      to: decisionEmailRecipient || 'No parent email on file',
      subject: `Additional Information Required — ${familyName} (${applicationCode})`,
      message: [
        `The financial aid office needs a little more information to continue reviewing the ${familyName} family application (${applicationCode}).`,
        `Requested information: ${requestedInfo}`,
        'Please use the parent portal to review the request and upload any documents.',
      ].join('\n\n'),
    };
  }, [applicationId, code, decisionEmailRecipient, familyName, moreInfoMessage]);

  const closeCompleteModal = () => {
    setIsCompleteModalOpen(false);
    setDecisionEmailPreviewKind(null);
    setDecisionEmailConfirmed(false);
    setMoreInfoErrorMessage('');
    setAwardErrorMessage('');
  };

  const showMoreInfoError = (message: string) => {
    setMoreInfoErrorMessage(message);
  };

  const handleAssignTo = async (option: { id: string | null; name: string } | null) => {
    if (!applicationId) return;
    setIsAssignMenuOpen(false);
    if (option && !option.id) {
      setToast({ tone: 'error', message: 'This staff record does not include a valid user ID yet.' });
      return;
    }
    const previous = assigneeName;
    setOptimisticAssigneeName(option?.name ?? '');
    try {
      await assignMutation.mutateAsync({
        applicationId,
        assignedTo: option ? option.id : null,
        assigneeName: option?.name ?? null,
      });
      setToast({ tone: 'success', message: option ? `Assigned to ${option.name}.` : 'Assignment cleared.' });
      void refetch();
    } catch (error) {
      setOptimisticAssigneeName(previous || null);
      const message = error instanceof Error ? error.message : 'Unable to update the assignment right now.';
      setToast({ tone: 'error', message });
    }
  };

  // INT-819: keep the assign menu keyboard navigable, mirroring the dashboard
  // actions menu (Escape closes; Arrow/Home/End move focus and keep the
  // active option scrolled into view inside the scrollable list).
  const handleAssignMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsAssignMenuOpen(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const container = assignMenuRef.current;
    if (!container) return;
    const menuItems = Array.from(
      container.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
    );
    if (menuItems.length === 0) return;
    event.preventDefault();
    const activeIndex = menuItems.findIndex((item) => item === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? menuItems.length - 1
          : event.key === 'ArrowDown'
            ? (activeIndex + 1) % menuItems.length
            : activeIndex <= 0
              ? menuItems.length - 1
              : activeIndex - 1;
    const nextItem = menuItems[nextIndex];
    if (!nextItem) return;
    nextItem.focus({ preventScroll: true });
    nextItem.scrollIntoView?.({ block: 'nearest' });
  };

  const handleRequestMoreInfo = async (options: DecisionSubmitOptions = {}) => {
    if (!applicationId) return;
    const message = moreInfoMessage.trim();
    if (!message) {
      showMoreInfoError('Add a short note describing what the family should provide.');
      return;
    }
    if (options.sendExternalParentEmail && !moreInfoEmailConfirmed) {
      showMoreInfoError('Please confirm the email preview before queueing a parent email.');
      return;
    }
    if (options.sendExternalParentEmail && !decisionEmailRecipient) {
      showMoreInfoError('Add a parent email before queueing this message.');
      return;
    }
    if (!canRequestMoreInfoFromStatus(displayStatusToken)) {
      showMoreInfoError(buildRequestMoreInfoUnavailableMessage(displayStatusToken));
      return;
    }
    setMoreInfoErrorMessage('');
    setBusyAction('more-info');
    try {
      await requestMoreInfoMutation.mutateAsync({
        applicationId,
        message,
        ...buildExternalParentEmailApprovalPayload(Boolean(options.sendExternalParentEmail)),
      });
      setOptimisticStatusToken(normalizeStatusToken('more_info_needed'));
      setToast({
        tone: 'success',
        message: options.sendExternalParentEmail
          ? `${familyName} moved to More Info Needed and parent email queued.`
          : `${familyName} moved to More Info Needed. Parent email was not sent.`,
      });
      setIsMoreInfoMode(false);
      setMoreInfoMessage('');
      setMoreInfoEmailConfirmed(false);
      closeCompleteModal();
      void refetch();
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : '';
      const transitionStatusToken = readRequestMoreInfoTransitionStatus(rawMessage);
      const fallback = isRequestMoreInfoTransitionError(rawMessage)
        ? buildRequestMoreInfoUnavailableMessage(transitionStatusToken || displayStatusToken)
        : (rawMessage || 'Unable to request more information right now.');
      showMoreInfoError(fallback);
    } finally {
      setBusyAction(null);
    }
  };

  const openDecisionEmailPreview = (kind: DecisionEmailKind) => {
    setDecisionEmailPreviewKind(kind);
    setDecisionEmailConfirmed(false);
  };

  const handleApprove = async (options: DecisionSubmitOptions = {}) => {
    if (!applicationId) return;
    // INT-1127: an award that differs from the recommendation is an override,
    // which the decision backend requires to carry a written justification.
    const awardDecision = resolveAwardEntryDecision({
      percentInput: awardPercentInput,
      recommendedPercent,
      eligibleTuition,
      justification: awardJustification,
    });
    if (!awardDecision.ok) {
      setAwardErrorMessage(awardDecision.message);
      setToast({ tone: 'error', message: awardDecision.message });
      return;
    }
    setAwardErrorMessage('');
    setBusyAction('approve');
    try {
      const result = await makeDecision.mutateAsync({
        applicationId,
        decisionType: awardDecision.decisionType,
        awardedPercent: awardDecision.awardedPercent,
        ...(awardDecision.decisionType === 'override'
          ? { overrideJustification: awardDecision.overrideJustification ?? '' }
          : {}),
        ...buildExternalParentEmailApprovalPayload(Boolean(options.sendExternalParentEmail)),
      });
      setOptimisticStatusToken(normalizeStatusToken(asRecord(result as unknown).status || 'approved'));
      const awardSummary = hasEligibleTuition
        ? `${formatAwardCurrency(awardDecision.awardedAmount)} (${formatAwardPercentLabel(awardDecision.awardedPercent)})`
        : formatAwardPercentLabel(awardDecision.awardedPercent);
      setToast({
        tone: 'success',
        message: options.sendExternalParentEmail
          ? `Application approved and parent email queued. Scholarship recorded at ${awardSummary}.`
          : `Application successfully approved. Scholarship recorded at ${awardSummary}.`,
      });
      closeCompleteModal();
      refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to approve the application right now.';
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  };

  const handleDeny = async (options: DecisionSubmitOptions = {}) => {
    if (!applicationId) return;
    setBusyAction('deny');
    try {
      const result = await makeDecision.mutateAsync({
        applicationId,
        decisionType: 'deny',
        denialReason: 'staff_review',
        ...buildExternalParentEmailApprovalPayload(Boolean(options.sendExternalParentEmail)),
      });
      setOptimisticStatusToken(normalizeStatusToken(asRecord(result as unknown).status || 'denied'));
      setToast({
        tone: 'success',
        message: options.sendExternalParentEmail
          ? 'Application declined and parent email queued.'
          : 'Application successfully declined.',
      });
      closeCompleteModal();
      refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to decline the application right now.';
      setToast({ tone: 'error', message });
    } finally {
      setBusyAction(null);
    }
  };

  const handleQueueDecisionEmail = () => {
    if (!decisionEmailPreview) return;
    if (!decisionEmailConfirmed) {
      setToast({ tone: 'error', message: 'Please confirm the email preview before queueing a parent email.' });
      return;
    }
    if (!decisionEmailRecipient) {
      setToast({ tone: 'error', message: 'Add a parent email before queueing this message.' });
      return;
    }

    if (decisionEmailPreview.kind === 'approve') {
      handleApprove({ sendExternalParentEmail: true });
      return;
    }

    handleDeny({ sendExternalParentEmail: true });
  };

  const handleContactSend = () => {
    const subject = readText(contactSubject, `Financial Aid Update - ${familyName}`);
    const message = readText(contactMessage);
    if (!message) {
      setToast({ tone: 'error', message: 'Please enter a message before sending.' });
      return;
    }
    if (!primaryEmail) {
      setToast({ tone: 'error', message: 'Primary contact email is not available for this application.' });
      return;
    }
    if (typeof window === 'undefined') {
      setToast({ tone: 'error', message: 'Unable to open your mail client in this environment.' });
      return;
    }
    const mailto = `mailto:${encodeURIComponent(primaryEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
    window.open(mailto, '_blank', 'noopener,noreferrer');
    setToast({ tone: 'success', message: 'Draft opened in your mail client.' });
    setContactSubject('');
    setContactMessage('');
    setIsContactModalOpen(false);
  };

  useEffect(() => {
    if (!toast) return () => {};
    const timeoutId = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  useEffect(() => {
    setOptimisticStatusToken(null);
  }, [applicationId]);

  useEffect(() => {
    if (!isAssignMenuOpen) return () => {};
    const frame = requestAnimationFrame(() => {
      const first = assignMenuRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
      first?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isAssignMenuOpen]);

  useEffect(() => {
    const node = detailRootRef.current;
    if (!node) return () => {};

    // INT-804: measure the parent surface, not the detail root itself. Compact
    // mode caps the root at max-w-[760px], so measuring the root would keep
    // reporting <=760px and latch the compact layout after the viewport
    // returns to desktop width.
    const surface = node.parentElement ?? node;

    const syncWidth = () => {
      const surfaceWidth = Math.round(surface.getBoundingClientRect().width);
      setDetailSurfaceWidth(surfaceWidth);
    };

    syncWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncWidth);
      return () => window.removeEventListener('resize', syncWidth);
    }

    const observer = new ResizeObserver(syncWidth);
    observer.observe(surface);
    return () => observer.disconnect();
  }, []);
  return (
    <section
      ref={detailRootRef}
      data-testid="financial-aid-staff-detail-root"
      data-extracted-from="owned:ystnj/financial-aid/v2-1/staff-detail"
      data-loaded={review ? 'true' : 'false'}
      data-layout-mode={layoutMode}
      className={`flex ${rootLayoutClass} ${rootWidthClass} bg-background-light text-irongrey font-sans relative min-h-[calc(100vh-64px)]`}
    >
      {toast && typeof document !== 'undefined'
        ? createPortal(
          <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} />,
          document.body
        )
        : null}

      <div className={`${mobileHeaderClass} flex-col bg-background-light border-b border-gray-200 sticky top-0 z-40`}>
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/school/financialaid/staff')}
            className="flex items-center gap-1 text-xs font-bold text-irongrey/60 hover:text-primary transition-colors"
          >
            <ArrowLeft size={14} />
            Back
          </button>
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: (statusConfig[displayStatusToken] ?? statusConfig.NEW).dot }}
            />
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: (statusConfig[displayStatusToken] ?? statusConfig.NEW).text }}
            >
              {toStatusLabel(displayStatusToken)}
            </span>
          </div>
        </div>
        <div className="px-4 pb-3">
          <ModuleSectionNav
            items={sectionNavItems}
            activeId={activeTab.id}
            ariaLabel="Financial aid detail sections"
            compact={isCompactLayout}
            renderDesktop={false}
            triggerClassName="border-0 active:bg-gray-50"
            mobileMenuTitle="Navigate To..."
            mobileCloseLabel="Close mobile navigation menu"
            testIds={{
              mobileTrigger: 'financial-aid-detail-mobile-menu-trigger',
              mobileDialog: 'financial-aid-detail-mobile-menu',
              mobileItemPrefix: 'financial-aid-detail-mobile-nav',
            }}
          />
        </div>
      </div>

      <nav
        data-testid="financial-aid-detail-sidebar"
        className={`${sidebarClass} w-[200px] bg-background-light border-r flex-shrink-0 overflow-y-auto flex-col px-2`}
        style={{ borderColor: '#F3F4F6' }}
      >
        <button
          type="button"
          onClick={() => navigate('/school/financialaid/staff')}
          className="flex items-center gap-1 text-sm mb-8 font-medium px-2 text-left"
          style={{ color: '#404F4D' }}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="space-y-1">
          {sidebarTabs.map((tab) => (
            <NavLink
              key={tab.id}
              to={tab.id}
              data-testid={`financial-aid-detail-tab-${tab.id}`}
              style={{ fontSize: "smaller" }}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors mb-1 ${isActive ? 'bg-white text-jungle font-bold shadow-sm' : 'text-[#6B7280] hover:bg-[#ffffffd1]'}`}
            >
              <tab.icon size={18} />
              <span className="flex-1">{tab.label}</span>
              {tab.badge ? (
                <span className="ml-auto text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center text-white bg-vibrant-coral shadow-sm shadow-vibrant-coral/20">
                  {tab.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header
          data-testid="financial-aid-detail-header"
          className={`${headerPaddingClass} border-b bg-background-light flex-shrink-0`}
          style={{ borderColor: '#F3F4F6', paddingTop: 0 }}
        >
          <h1 className="sr-only">Financial Aid application detail: {familyName}</h1>
          <div className={`flex ${headerContentClass} justify-between gap-4`}>
            <div className="min-w-0 flex-1">
              <div className={`flex min-w-0 max-w-full ${titleStatusRowClass}`}>
                <h2
                  data-testid="financial-aid-detail-heading"
                  className={`${titleClass} ${titleWidthClass} whitespace-normal break-normal font-bold text-[#111827]`}
                  title={familyName}
                >
                  {familyName}
                </h2>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: (statusConfig[displayStatusToken] ?? statusConfig.NEW).dot }}
                  />
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{ color: (statusConfig[displayStatusToken] ?? statusConfig.NEW).text }}
                  >
                    {toStatusLabel(displayStatusToken)}
                  </span>
                </div>
              </div>
              <p className={`${metaClass} mt-1`} style={{ color: '#9CA3AF' }}>
                REF: {code} {submittedDate ? `• Submitted ${submittedDate}` : ''}
              </p>
            </div>
            <div className={`flex items-center ${actionRowClass} relative min-w-0 flex-wrap`}>
              <FinancialAidCorrectionHeaderActions
                controller={correctionController}
                buttonClassName={actionButtonClass}
              />
              <div className="relative">
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isAssignMenuOpen}
                  onClick={() => setIsAssignMenuOpen((open) => !open)}
                  className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 rounded-lg whitespace-nowrap ${actionButtonClass}`}
                >
                  <UserPlus size={16} />
                  <span className="min-w-0 max-w-[10rem] truncate">{assigneeName || 'Assign to...'}</span>
                </button>
                {isAssignMenuOpen ? (
                  <div
                    ref={assignMenuRef}
                    role="menu"
                    aria-label="Assign financial aid application"
                    onKeyDown={handleAssignMenuKeyDown}
                    className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-gray-100 bg-white py-1.5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)]"
                  >
                    <div className="max-h-[280px] overflow-y-auto">
                      {assigneeName ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => void handleAssignTo(null)}
                          className="w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 text-[#6B7280]"
                        >
                          Unassign
                        </button>
                      ) : null}
                      {staffAssignOptions.length > 0 ? (
                        staffAssignOptions.map((option) => (
                          <button
                            key={option.name}
                            type="button"
                            role="menuitem"
                            disabled={!option.id}
                            title={!option.id ? 'Staff record does not include a valid user ID' : undefined}
                            onClick={() => void handleAssignTo(option)}
                            className={`w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${assigneeName === option.name ? 'text-primary bg-primary/5' : 'text-[#111827]'}`}
                          >
                            {option.name}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-xs text-[#6B7280]">
                          Staff assignments are syncing.
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setIsContactModalOpen(true)}
                className={`flex items-center gap-2 text-primary font-medium hover:text-primary-hover transition-all bg-primary/5 rounded-lg whitespace-nowrap ${actionButtonClass}`}
              >
                <MailPlus size={16} />
                <span className="hidden sm:inline">Contact</span>
                <span className="sm:hidden">Msg</span>
              </button>
              <button
                type="button"
                onClick={() => setIsCompleteModalOpen(true)}
                disabled={busyAction != null || isCorrectionEditing}
                className={`flex items-center gap-2 text-primary font-medium transition-all bg-primary/5 rounded-lg whitespace-nowrap ${actionButtonClass} ${busyAction || isCorrectionEditing ? 'opacity-70 cursor-not-allowed' : 'hover:text-primary-hover'}`}
              >
                <CheckCircle size={16} />
                <span className="hidden sm:inline">Complete</span>
                <span className="sm:hidden">Done</span>
              </button>
            </div>
          </div>
          {isCorrectionEditing && !isCorrectionsTabActive ? correctionEditor : null}
          {detail.isLoading ? (
            <p className="text-xs text-[#6B7280] mt-3">Loading application...</p>
          ) : null}
          {detail.error ? (
            <SupportErrorMessage className="mt-3">
              Unable to load this application.
            </SupportErrorMessage>
          ) : null}
        </header>

        <main className={`flex-1 overflow-y-auto ${mainPaddingClass} bg-background-light`}>
          <Outlet context={ctx} />
        </main>
      </div>

      {isCompleteModalOpen ? (
        <AppDialogFrame
          open={isCompleteModalOpen}
          onClose={closeCompleteModal}
          title="Complete Application"
          description={`Choose an outcome for the ${familyName} family application.`}
          testId="financial-aid-complete-application-modal"
          maxWidthClassName="max-w-2xl"
          titleClassName="text-xl font-semibold text-[#111827]"
          descriptionClassName="text-sm text-[#6B7280]"
          bodyClassName="space-y-4"
          bodyPaddingClassName="p-8"
          footerPaddingClassName="px-8 py-6"
          footerClassName="justify-center"
          footer={(
            <button
              type="button"
              className="text-sm text-[#404F4D] hover:underline"
              onClick={closeCompleteModal}
            >
              Cancel
            </button>
          )}
        >
          <div className="rounded-xl border-2 border-[#E5E7EB] p-5" data-testid="financial-aid-award-entry">
            <h3 className="text-base font-semibold text-[#111827]">Scholarship award</h3>
            <p className="mt-1 text-sm text-[#6B7280]">
              {hasEligibleTuition
                ? `Enter the scholarship granted to this family. Eligible tuition is ${formatAwardCurrency(eligibleTuition)}.`
                : 'Enter the scholarship granted to this family as a percentage of eligible tuition. Eligible tuition is not available for this application yet, so the dollar amount cannot be calculated here.'}
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                  Award amount (USD)
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={awardAmountInput}
                  onChange={(event) => handleAwardAmountChange(event.currentTarget.value)}
                  disabled={busyAction != null || !hasEligibleTuition}
                  data-testid="financial-aid-award-amount-input"
                  aria-label="Award amount in dollars"
                  placeholder={hasEligibleTuition ? '0.00' : 'Not available'}
                  className="mt-2 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm text-[#111827] focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:bg-[#FAFBF8] disabled:text-[#9CA3AF]"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                  Award percent of eligible tuition
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={awardPercentInput}
                  onChange={(event) => handleAwardPercentChange(event.currentTarget.value)}
                  disabled={busyAction != null}
                  data-testid="financial-aid-award-percent-input"
                  aria-label="Award percent of eligible tuition"
                  placeholder="0"
                  className="mt-2 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm text-[#111827] focus:border-primary focus:outline-none disabled:cursor-not-allowed"
                />
              </label>
            </div>

            <p className="mt-3 text-sm text-[#404F4D]" data-testid="financial-aid-award-summary">
              {awardPercentPreview == null || !isAwardWithinRange
                ? `Enter an award between ${AWARD_PERCENT_MIN}% and ${AWARD_PERCENT_MAX}% of eligible tuition.`
                : hasEligibleTuition
                  ? `The family will be awarded ${formatAwardCurrency(awardAmountPreview ?? 0)} — ${formatAwardPercentLabel(awardPercentPreview)} of ${formatAwardCurrency(eligibleTuition)} eligible tuition.`
                  : `The family will be awarded ${formatAwardPercentLabel(awardPercentPreview)} of eligible tuition.`}
            </p>
            <p className="mt-1 text-xs text-[#6B7280]">
              {isAwardOverride
                ? `This differs from the ${formatAwardPercentLabel(recommendedPercent)} system recommendation, so it is recorded as an override.`
                : `This matches the ${formatAwardPercentLabel(recommendedPercent)} system recommendation.`}
            </p>

            {isAwardOverride ? (
              <label className="mt-4 block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                  Reason for changing the award
                  <span className="ml-2 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[9px] text-[#92400E]">
                    Required
                  </span>
                </span>
                <textarea
                  value={awardJustification}
                  onChange={(event) => {
                    setAwardJustification(event.currentTarget.value);
                    setAwardErrorMessage('');
                  }}
                  rows={2}
                  required
                  aria-required="true"
                  data-testid="financial-aid-award-justification"
                  placeholder="Explain why the awarded scholarship differs from the recommendation..."
                  className="mt-2 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm text-[#111827] focus:border-primary focus:outline-none"
                />
              </label>
            ) : null}

            {awardErrorMessage ? (
              <SupportErrorMessage
                className="mt-3"
                data-testid="financial-aid-award-error"
                showSupport={false}
              >
                {awardErrorMessage}
              </SupportErrorMessage>
            ) : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              className="group w-full rounded-xl border-2 border-[#E5E7EB] p-5 text-left transition-all hover:border-[#28E2A7] hover:bg-[#ECFDF5] disabled:cursor-not-allowed"
              onClick={() => handleApprove()}
              disabled={busyAction != null}
            >
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-[#ECFDF5] p-2 text-[#28E2A7] transition-colors group-hover:bg-white">
                  <CheckCircle size={24} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#111827]">Approve</h3>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    {awardPercentPreview != null && isAwardWithinRange
                      ? hasEligibleTuition
                        ? `Record the ${formatAwardCurrency(awardAmountPreview ?? 0)} scholarship entered above (${formatAwardPercentLabel(awardPercentPreview)} of eligible tuition). No parent email is queued.`
                        : `Record the ${formatAwardPercentLabel(awardPercentPreview)} scholarship entered above. No parent email is queued.`
                      : 'Record the scholarship award entered above. No parent email is queued.'}
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              className="group w-full rounded-xl border-2 border-[#E5E7EB] p-5 text-left transition-all hover:border-[#FF5F5F] hover:bg-[#FEE2E2] disabled:cursor-not-allowed"
              onClick={() => handleDeny()}
              disabled={busyAction != null}
            >
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-[#FEE2E2] p-2 text-[#FF5F5F] transition-colors group-hover:bg-white">
                  <XCircle size={24} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#111827]">Decline</h3>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    Deny this financial aid application. No parent email is queued.
                  </p>
                </div>
              </div>
            </button>
          </div>

          <div className="rounded-xl border-2 border-[#E5E7EB] p-5">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-[#FEF3C7] p-2 text-[#D97706]">
                <MailPlus size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-[#111827]">Request More Info</h3>
                <p className="mt-1 text-sm text-[#6B7280]">
                  Move this application to More Info Needed and notify the family what to provide.
                </p>
                {isMoreInfoMode ? (
                  <div className="mt-3 space-y-3">
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                        Requested information
                        <span className="ml-2 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[9px] text-[#92400E]">
                          Required
                        </span>
                      </span>
                      <textarea
                        value={moreInfoMessage}
                        onChange={(event) => {
                          setMoreInfoMessage(event.target.value);
                          setMoreInfoEmailConfirmed(false);
                          setMoreInfoErrorMessage('');
                        }}
                        rows={3}
                        required
                        aria-required="true"
                        data-testid="financial-aid-detail-more-info-message"
                        placeholder="Describe the documents or details the family should provide..."
                        className="mt-2 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm text-[#111827] focus:border-primary focus:outline-none"
                      />
                    </label>
                    {moreInfoErrorMessage ? (
                      <SupportErrorMessage
                        className="mt-3"
                        data-testid="financial-aid-detail-more-info-error"
                        showSupport={false}
                      >
                        {moreInfoErrorMessage}
                      </SupportErrorMessage>
                    ) : null}
                    <div
                      className="space-y-3 rounded-lg border border-[#E5E7EB] bg-white p-4"
                      data-testid="more-info-email-preview"
                    >
                      <p className="text-xs text-[#6B7280]">
                        Review the parent-facing message before queueing an external email.
                      </p>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Recipient</p>
                        <p className="mt-1 text-sm font-semibold text-[#111827]">{moreInfoEmailPreview.to}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Subject</p>
                        <p className="mt-1 text-sm font-semibold text-[#111827]">{moreInfoEmailPreview.subject}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Message</p>
                        <p className="mt-1 whitespace-pre-line text-sm text-[#6B7280]">{moreInfoEmailPreview.message}</p>
                      </div>
                      <label className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#FAFBF8] p-3 text-sm text-[#404F4D]">
                        <input
                          type="checkbox"
                          checked={moreInfoEmailConfirmed}
                          onChange={(event) => setMoreInfoEmailConfirmed(event.currentTarget.checked)}
                          className="mt-1 h-4 w-4 rounded border-[#D1D5DB] text-primary focus:ring-primary"
                        />
                        <span>
                          I reviewed this parent-facing message and approve queueing an external email for this
                          more-info request.
                        </span>
                      </label>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => void handleRequestMoreInfo()}
                        disabled={busyAction != null}
                        className="rounded-lg border border-primary/20 bg-white px-4 py-2 text-sm font-semibold text-primary transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busyAction === 'more-info' ? 'Saving...' : 'Move without email'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleRequestMoreInfo({ sendExternalParentEmail: true })}
                        disabled={busyAction != null || !moreInfoEmailConfirmed || !decisionEmailRecipient}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                          busyAction != null || !moreInfoEmailConfirmed || !decisionEmailRecipient
                            ? 'cursor-not-allowed bg-gray-300 text-white'
                            : 'bg-primary text-white hover:bg-primary-hover'
                        }`}
                      >
                        <Send size={16} />
                        {busyAction === 'more-info' ? 'Queueing...' : 'Queue Email'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsMoreInfoMode(false);
                          setMoreInfoMessage('');
                          setMoreInfoEmailConfirmed(false);
                          setMoreInfoErrorMessage('');
                        }}
                        disabled={busyAction != null}
                        className="text-sm text-[#404F4D] hover:underline disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMoreInfoMode(true);
                      setMoreInfoEmailConfirmed(false);
                      setMoreInfoErrorMessage('');
                    }}
                    disabled={busyAction != null}
                    className="mt-3 rounded-lg border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#111827] transition-colors hover:border-[#D97706] hover:bg-[#FFFBEB] disabled:cursor-not-allowed"
                  >
                    Request more info...
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFBF8] p-4">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-bold text-[#111827]">Optional Parent Email</h3>
              <p className="text-xs text-[#6B7280]">
                Queue a parent-facing decision email only after reviewing the same recipient and message fields used by the outbound status email.
              </p>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => openDecisionEmailPreview('approve')}
                disabled={busyAction != null}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                  decisionEmailPreviewKind === 'approve'
                    ? 'border-[#28E2A7] bg-[#ECFDF5] text-[#237062]'
                    : 'border-[#E5E7EB] bg-white text-[#404F4D] hover:border-[#28E2A7]'
                }`}
              >
                Preview approval email
              </button>
              <button
                type="button"
                onClick={() => openDecisionEmailPreview('deny')}
                disabled={busyAction != null}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                  decisionEmailPreviewKind === 'deny'
                    ? 'border-[#FF5F5F] bg-[#FEE2E2] text-[#FE2C3B]'
                    : 'border-[#E5E7EB] bg-white text-[#404F4D] hover:border-[#FF5F5F]'
                }`}
              >
                Preview denial email
              </button>
            </div>

            {decisionEmailPreview ? (
              <div
                className="mt-4 space-y-3 rounded-lg border border-[#E5E7EB] bg-white p-4"
                data-testid="decision-email-preview"
              >
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Recipient</p>
                    <p className="mt-1 font-semibold text-[#111827]">{decisionEmailPreview.to}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Current Step</p>
                    <p className="mt-1 font-semibold text-[#111827]">{decisionEmailPreview.statusLabel}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Subject</p>
                  <p className="mt-1 text-sm font-semibold text-[#111827]">{decisionEmailPreview.subject}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Message</p>
                  <p className="mt-1 text-sm text-[#6B7280]">{decisionEmailPreview.message}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Action</p>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    <span className="font-semibold text-[#111827]">{decisionEmailPreview.actionHeading}:</span>{" "}
                    {decisionEmailPreview.actionBody}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-[#237062]">{decisionEmailPreview.actionLabel}</p>
                </div>
                <label className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] bg-[#FAFBF8] p-3 text-sm text-[#404F4D]">
                  <input
                    type="checkbox"
                    checked={decisionEmailConfirmed}
                    onChange={(event) => setDecisionEmailConfirmed(event.currentTarget.checked)}
                    className="mt-1 h-4 w-4 rounded border-[#D1D5DB] text-primary focus:ring-primary"
                  />
                  <span>I reviewed the recipient and parent-facing message preview and approve queueing this parent email.</span>
                </label>
                <button
                  type="button"
                  onClick={handleQueueDecisionEmail}
                  disabled={busyAction != null || !decisionEmailConfirmed || !decisionEmailRecipient}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all ${
                    busyAction != null || !decisionEmailConfirmed || !decisionEmailRecipient
                      ? 'cursor-not-allowed bg-gray-300 text-white'
                      : 'bg-primary text-white hover:bg-primary-hover'
                  }`}
                >
                  <Send size={16} />
                  {decisionEmailPreview.kind === 'approve' ? 'Approve and queue email' : 'Decline and queue email'}
                </button>
              </div>
            ) : null}
          </div>
        </AppDialogFrame>
      ) : null}

      {isContactModalOpen ? (
        <AppDialogFrame
          open={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          title="Contact Family"
          description="Send a message to the primary contact."
          testId="financial-aid-contact-family-modal"
          maxWidthClassName="max-w-lg"
          panelClassName="rounded-3xl"
          titleClassName="text-2xl font-bold text-[#111827]"
          descriptionClassName="text-sm font-medium text-[#9CA3AF]"
          bodyClassName="space-y-6"
          bodyPaddingClassName="px-8 pb-8 pt-0"
          footerPaddingClassName="px-8 py-6"
          footerClassName="justify-between"
          footer={(
            <>
              <button
                type="button"
                onClick={() => setIsContactModalOpen(false)}
                className="text-sm font-bold text-irongrey/40 transition-colors hover:text-irongrey"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleContactSend}
                disabled={!contactSubject.trim() || !contactMessage.trim()}
                className={`flex items-center gap-2 rounded-2xl px-8 py-3 text-sm font-bold shadow-lg transition-all ${!contactSubject.trim() || !contactMessage.trim() ? 'cursor-not-allowed bg-gray-300 text-white' : 'bg-primary text-white shadow-primary/20 hover:bg-primary-hover'}`}
              >
                <Send size={18} />
                Send Message
              </button>
            </>
          )}
        >
          <div className="rounded-2xl border border-gray-100 bg-[#FAFBF8] p-6">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Primary Contact</p>
            <h3 className="text-lg font-bold text-[#111827]">{primaryContactName}</h3>
            <div className="mt-2 space-y-0.5 text-sm text-[#6B7280]">
              <p>{primaryEmail}</p>
              <p>{primaryPhone}</p>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Subject Line</label>
            <input
              type="text"
              value={contactSubject}
              onChange={(event) => setContactSubject(event.currentTarget.value)}
              className="w-full rounded-xl border border-gray-100 bg-[#FAFBF8] px-4 py-3 text-sm text-irongrey placeholder:text-irongrey/30 focus:border-primary focus:ring-primary"
              placeholder="Enter subject..."
            />
          </div>

          <div>
            <label className="mb-2 block text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Message</label>
            <textarea
              value={contactMessage}
              onChange={(event) => setContactMessage(event.currentTarget.value)}
              className="min-h-[160px] w-full resize-none rounded-xl border border-gray-100 bg-[#FAFBF8] p-4 text-sm text-irongrey focus:border-primary focus:ring-primary"
              placeholder="Type your message here..."
            />
          </div>
        </AppDialogFrame>
      ) : null}
    </section>
  );
}
